import { IdentityStorageDto } from "./identity-storage-dto";
import { inject, singleton } from "tsyringe";
import { InstantConfigService } from "../../InstantConfig";
import { IdentityStorageClient } from "./identity-storage-client";
import { reportError } from "@identity/utils";

@singleton()
export class IdentityStorageService {
  constructor(
    @inject(InstantConfigService)
    private instantConfigService: InstantConfigService,
    @inject(IdentityStorageClient)
    private identityStorageClient: IdentityStorageClient,
  ) {}

  async get(): Promise<IdentityStorageDto> {
    const localData = this.identityStorageClient.getLocalData();

    if (
      localData &&
      !localData.synced &&
      this.shouldRetryIdentitySync(localData)
    ) {
      this.setRemote(localData).catch((error) => {
        void reportError(error);
      });
    } else if (!localData || this.isIdentityDeprecated(localData)) {
      return this.getRemote();
    }

    return localData;
  }

  private isIdentityDeprecated(localData: IdentityStorageDto): boolean {
    // Fallback : days * hours * minutes * seconds * milliseconds;
    const timeToLive = this.instantConfigService.get(
      "icIdentityTtl",
      7 * 24 * 60 * 60 * 1000,
    );
    const lastDeprecatedTs = Date.now() - timeToLive;
    return !localData.timestamp || localData.timestamp < lastDeprecatedTs;
  }

  private shouldRetryIdentitySync(localData: IdentityStorageDto): boolean {
    const hourMs = 60 * 60 * 1000;
    const shouldRetryBeforeTs = Date.now() - hourMs;

    return (
      !localData.lastSyncAttemptTimestamp ||
      localData.lastSyncAttemptTimestamp < shouldRetryBeforeTs
    );
  }

  private async getRemote(): Promise<IdentityStorageDto> {
    const remoteData = await this.identityStorageClient.fetchData();
    this.identityStorageClient.setLocalData({
      ...remoteData,
      timestamp: Date.now(),
    });
    return remoteData;
  }

  private async setRemote(data: IdentityStorageDto) {
    const remoteData = await this.identityStorageClient.postData(data);
    const localData = this.identityStorageClient.getLocalData();

    this.identityStorageClient.setLocalData({
      ppid: localData.ppid,
      synced: remoteData.synced,
      timestamp: Date.now(),
      ...(remoteData.synced ? {} : { lastSyncAttemptTimestamp: Date.now() }),
    });
  }
}
